import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import OnboardingWelcome from './OnboardingWelcome';
import OnboardingBirthYear from './OnboardingBirthYear';
import OnboardingChaptersReady from './OnboardingChaptersReady';
import { useChaptersData } from '../../hooks/useChaptersData';

/**
 * ChaptersOnboarding - Main onboarding flow for My Life Chapters
 *
 * If DOB was collected at registration, skips the birth year screen:
 *   Welcome → (auto-generate chapters) → Chapters Ready
 * If DOB is missing, falls back to the original 3-step flow:
 *   Welcome → Birth Year → Chapters Ready
 */
function ChaptersOnboarding({ userName, birthYear: propBirthYear }) {
  const navigate = useNavigate();
  const { generateChapters, loading, error } = useChaptersData();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    birthYear: propBirthYear || null,
    hometown: '',
  });
  const [generatedChapters, setGeneratedChapters] = useState([]);

  // Handle Welcome → auto-generate if we already have DOB, else go to birth year screen
  const handleWelcomeNext = async () => {
    if (formData.birthYear) {
      // DOB collected at registration — skip birth year screen, generate immediately
      const result = await generateChapters(formData.birthYear, '');
      if (result) {
        setGeneratedChapters(result);
        setStep(3);
      }
    } else {
      // No DOB — show birth year screen
      setStep(2);
    }
  };

  // Handle birth year screen → generate chapters (fallback path)
  const handleBirthYearNext = async ({ birthYear, hometown }) => {
    setFormData({ birthYear, hometown });
    const result = await generateChapters(birthYear, hometown);
    if (result) {
      setGeneratedChapters(result);
      setStep(3);
    }
  };

  const handleBirthYearBack = () => {
    setStep(1);
  };

  const handleStartChapter = (chapterId) => {
    navigate(`/my-life/chapters/${chapterId}`);
  };

  // Loading state (generating chapters)
  if (loading && (step === 1 || step === 2)) {
    return (
      <div className="min-h-screen bg-fb-cream flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fb-olive mx-auto mb-4"></div>
          <p className="font-serif text-lg text-gray-600">
            Creating your chapters...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-fb-cream flex items-center justify-center p-6">
        <div className="bg-white rounded-xl border border-red-200 p-8 text-center max-w-md">
          <span className="text-5xl mb-4 block">😔</span>
          <h3 className="font-serif font-bold text-xl text-gray-800 mb-2">
            Something went wrong
          </h3>
          <p className="font-serif text-gray-500 text-base mb-4">
            We couldn't create your chapters. Please try again.
          </p>
          <button
            onClick={() => setStep(formData.birthYear ? 1 : 2)}
            className="bg-fb-olive hover:bg-fb-olive/90 text-white font-bold py-2 px-6 rounded-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  switch (step) {
    case 1:
      return (
        <OnboardingWelcome
          userName={userName}
          onNext={handleWelcomeNext}
        />
      );

    case 2:
      return (
        <OnboardingBirthYear
          onNext={handleBirthYearNext}
          onBack={handleBirthYearBack}
        />
      );

    case 3:
      return (
        <OnboardingChaptersReady
          userName={userName}
          birthYear={formData.birthYear}
          hometown={formData.hometown}
          chapters={generatedChapters}
          onStartChapter={handleStartChapter}
        />
      );

    default:
      return null;
  }
}

ChaptersOnboarding.propTypes = {
  userName: PropTypes.string,
  birthYear: PropTypes.number,
};

ChaptersOnboarding.defaultProps = {
  userName: 'Friend',
  birthYear: null,
};

export default ChaptersOnboarding;
